import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { push } from 'connected-react-router'
import { ApiMiddlewarePayload } from '@ocean/api'
import { localization } from '@ocean/configs'
import { FilterKeyType } from '@ocean/types/dist/filter'
import { CommonUtils } from '@ocean/utils'
import { AppThunk } from 'store'
import { ApiAction, MiddlewareApiAction, createApiAction } from 'api'
import { Person } from 'core/people/types'
import { userLoggedOut } from 'core/session/slices/session'
import { RangePeriod } from 'components/common/BigCalendar/types'
import { BigCalendarUtils } from 'components/common/BigCalendar/utils'
import { getListInstructorLedEnrollments } from 'modules/simulatorTrainings/common/api/simulatorTrainingService'
import { namespace } from 'modules/simulatorTrainings/common/localization'
import { listInstructorLedEnrollmentsLoaded } from 'modules/simulatorTrainings/common/slices/instructor'
import { ApiListInstructorLedEnrollmentsResponse } from 'modules/simulatorTrainings/common/slices/instructor/types'
import {
  SpecialUser,
  SimulatorTrainingPersonType,
  SimulatorTrainingScheduleSession,
  SimulatorTrainingMaintenancePeriod,
  SimulatorTrainingSessionType,
  SimulatorTrainingScheduleSessionEvent,
} from 'modules/simulatorTrainings/common/types'
import { SimulatorInstructorLedEnrolledPerson } from 'modules/simulatorTrainings/common/types/instructor'
import { SimulatorTrainingUtils } from 'modules/simulatorTrainings/common/utils'
import {
  getListSimulatorTrainingSchedules,
  getListScheduleMaintenanceAndSessions,
  addSimulatorTrainingSchedule,
  demandSimulatorTrainingScheduleSession,
  updateSimulatorTrainingEnrollments,
  deleteSimulatorTrainingSchedule,
  updateSimulatorTrainingSchedule,
  updateSimulatorTrainingScheduleSessions,
} from 'modules/simulatorTrainings/instructor/instructorLed/Schedules/api/schedulesService'
import schedulesRoutes from 'modules/simulatorTrainings/instructor/instructorLed/Schedules/routing/routes'
import { UpdateSimulatorTrainingScheduleSessionsRequest } from 'modules/simulatorTrainings/instructor/instructorLed/Schedules/types'
import {
  ScheduleStatus,
  ShowType,
  SchedulesTagFilterItem,
  SchedulesTagFilterItems,
  UserWorkstation,
  UserWorkstations,
  AddSimulatorTrainingScheduleModel,
  DemandSimulatorTrainingScheduleSessionModel,
  SimulatorTrainingEnrollment,
  UpdateSimulatorTrainingEnrollmentsModel,
  SimulatorTrainingSessionDate,
  ListScheduleSessionsModel,
  UpdateSessionSubject,
  BookingMode,
  SessionChanges,
} from 'modules/simulatorTrainings/instructor/instructorLed/Schedules/types'
import { SchedulesUtils } from 'modules/simulatorTrainings/instructor/instructorLed/Schedules/utils'
import { PlannerUtils } from 'modules/simulatorTrainings/instructor/instructorLed/Schedules/utils'
import simulatorTrainingInstructorILTrainingDetailsRoutes from 'modules/simulatorTrainings/instructor/instructorLed/TrainingDetails/routing/routes'
import {
  SchedulesState,
  ApiSchedulesListResponse,
  ApiCreateSimulatorTrainingScheduleResponse,
  ApiDemandSimulatorTrainingScheduleSessionResponse,
  ApiScheduleMaintenanceAndSessionsResponse,
} from './types'

const { i18n } = localization

const I18_PATH = `${namespace}:instructor.instructorLed.schedules`

const initialState: SchedulesState = {
  scheduleListData: {},
  schedulesTagFilters: {
    status: { keyType: FilterKeyType.Number, value: ScheduleStatus.Upcoming },
    showType: { keyType: FilterKeyType.Number, value: ShowType.Own },
  },
  scheduleMaintenanceAndSessionsData: {},
  scheduleCreatorData: {
    instructorWorkstations: {},
    studentWorkstations: {},
    addedStudents: {},
    addedInstructors: {},
    timeZone: undefined,
    subject: undefined,
    range: BigCalendarUtils.getDateMonthPeriod(new Date()),
    sessionDates: {},
    bookingMode: BookingMode.Schedule,
  },
  scheduleEditorData: {
    instructorWorkstations: {},
    studentWorkstations: {},
    addedStudents: {},
    addedInstructors: {},
    subject: undefined,
    timeZone: undefined,
    range: null,
    addedSessions: {},
    updatedSessions: {},
    removedSessions: {},
  },
  createSimulatorTrainingScheduleRequested: {},
  demandSimulatorTrainingScheduleSessionRequested: {},
  updateSimulatorTrainingEnrollmentsRequested: {},
  deleteScheduleRequested: {},
  updateScheduleRequested: {},
  updateScheduleSessionsRequested: {},
  foundScheduleOngoingSession: undefined,
}

const schedulesSlice = createSlice({
  name: 'simulatorTrainingInstructorILSchedules',
  initialState,
  reducers: {
    scheduleListLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiSchedulesListResponse>>
    ) {
      state.scheduleListData = action.payload
    },
    listScheduleMaintenanceAndSessionsLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiScheduleMaintenanceAndSessionsResponse>>
    ) {
      state.scheduleMaintenanceAndSessionsData = action.payload
    },
    clearScheduleList(state) {
      state.scheduleListData = initialState.scheduleListData
    },
    clearScheduleCreatorData(state) {
      state.scheduleCreatorData = initialState.scheduleCreatorData
    },
    clearScheduleEditorData(state) {
      state.scheduleEditorData = initialState.scheduleEditorData
    },
    setSchedulesTagFilters(state, action: PayloadAction<SchedulesTagFilterItem>) {
      const filter = state.schedulesTagFilters[action.payload.name]
      if (filter) filter.value = action.payload.value
    },
    setCreatorInstructorWorkstation(state, action: PayloadAction<UserWorkstation>) {
      const { userId, workstationId } = action.payload
      state.scheduleCreatorData.instructorWorkstations[userId] = workstationId
    },
    setCreatorInstructorWorkstations(state, action: PayloadAction<Record<string, string>>) {
      state.scheduleCreatorData.instructorWorkstations = {
        ...state.scheduleCreatorData.instructorWorkstations,
        ...action.payload,
      }
    },
    setCreatorStudentWorkstation(state, action: PayloadAction<UserWorkstation>) {
      const { userId, workstationId } = action.payload
      state.scheduleCreatorData.studentWorkstations[userId] = workstationId
    },
    setCreatorStudentWorkstations(state, action: PayloadAction<Record<string, string>>) {
      state.scheduleCreatorData.studentWorkstations = {
        ...state.scheduleCreatorData.studentWorkstations,
        ...action.payload,
      }
    },
    removeCreatorStudents(state, action: PayloadAction<Record<string, Person>>) {
      Object.keys(action.payload).forEach(studentId => {
        delete state.scheduleCreatorData.addedStudents[studentId]
        delete state.scheduleCreatorData.studentWorkstations[studentId]
      })
    },
    addCreatorStudents(state, action: PayloadAction<Record<string, Person>>) {
      state.scheduleCreatorData.addedStudents = {
        ...state.scheduleCreatorData.addedStudents,
        ...action.payload,
      }
    },
    removeCreatorInstructors(state, action: PayloadAction<Record<string, SpecialUser>>) {
      Object.keys(action.payload).forEach(instructorId => {
        delete state.scheduleCreatorData.addedInstructors[instructorId]
        delete state.scheduleCreatorData.instructorWorkstations[instructorId]
      })
    },
    addCreatorInstructors(state, action: PayloadAction<Record<string, SpecialUser>>) {
      state.scheduleCreatorData.addedInstructors = {
        ...state.scheduleCreatorData.addedInstructors,
        ...action.payload,
      }
    },
    setCreatorTimeZone(state, action: PayloadAction<string | undefined>) {
      state.scheduleCreatorData.timeZone = action.payload
    },
    setCreatorSubject(state, action: PayloadAction<string | undefined>) {
      state.scheduleCreatorData.subject = action.payload
    },
    setCreatorRange(state, action: PayloadAction<RangePeriod>) {
      state.scheduleCreatorData.range = action.payload
    },
    setCreatorSession(state, action: PayloadAction<SimulatorTrainingScheduleSession>) {
      const { sessionId } = action.payload
      state.scheduleCreatorData.sessionDates[sessionId] = action.payload
    },
    removeCreatorSession(state, action: PayloadAction<string>) {
      delete state.scheduleCreatorData.sessionDates[action.payload]
    },
    updateCreatorSessionSubject(state, action: PayloadAction<UpdateSessionSubject>) {
      const { sessionId, subject } = action.payload
      state.scheduleCreatorData.sessionDates[sessionId] = {
        ...state.scheduleCreatorData.sessionDates[sessionId],
        sessionName: subject,
      }
    },
    setCreatorBookingMode(state, action: PayloadAction<BookingMode>) {
      state.scheduleCreatorData.bookingMode = action.payload
    },
    createSimulatorTrainingScheduleRequested(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiCreateSimulatorTrainingScheduleResponse>>
    ) {
      state.createSimulatorTrainingScheduleRequested = action.payload
    },
    demandSimulatorTrainingScheduleSessionRequested(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiDemandSimulatorTrainingScheduleSessionResponse>>
    ) {
      state.demandSimulatorTrainingScheduleSessionRequested = action.payload
    },
    setEditorTimeZone(state, action: PayloadAction<string | undefined>) {
      state.scheduleEditorData.timeZone = action.payload
    },
    setEditorRange(state, action: PayloadAction<RangePeriod>) {
      state.scheduleEditorData.range = action.payload
    },
    setEditorSubject(state, action: PayloadAction<string | undefined>) {
      state.scheduleEditorData.subject = action.payload
    },
    setEditorInstructorWorkstations(
      state,
      action: PayloadAction<Record<string, string | undefined>>
    ) {
      state.scheduleEditorData.instructorWorkstations = {
        ...state.scheduleEditorData.instructorWorkstations,
        ...action.payload,
      }
    },
    addEditorInstructorWorkstation(state, action: PayloadAction<UserWorkstation>) {
      const { userId, workstationId } = action.payload
      state.scheduleEditorData.instructorWorkstations[userId] = workstationId
    },
    setEditorStudentWorkstations(state, action: PayloadAction<Record<string, string | undefined>>) {
      state.scheduleEditorData.studentWorkstations = {
        ...state.scheduleEditorData.studentWorkstations,
        ...action.payload,
      }
    },
    addEditorStudentWorkstation(state, action: PayloadAction<UserWorkstation>) {
      const { userId, workstationId } = action.payload
      state.scheduleEditorData.studentWorkstations[userId] = workstationId
    },
    removeEditorStudents(
      state,
      action: PayloadAction<Record<string, SimulatorInstructorLedEnrolledPerson>>
    ) {
      Object.keys(action.payload).forEach(studentId => {
        delete state.scheduleEditorData.addedStudents[studentId]
        delete state.scheduleEditorData.studentWorkstations[studentId]
      })
    },
    addEditorStudents(
      state,
      action: PayloadAction<Record<string, SimulatorInstructorLedEnrolledPerson>>
    ) {
      state.scheduleEditorData.addedStudents = {
        ...state.scheduleEditorData.addedStudents,
        ...action.payload,
      }
    },
    setEditorStudents(
      state,
      action: PayloadAction<Record<string, SimulatorInstructorLedEnrolledPerson>>
    ) {
      state.scheduleEditorData.addedStudents = action.payload
    },
    removeEditorInstructors(
      state,
      action: PayloadAction<Record<string, SimulatorInstructorLedEnrolledPerson>>
    ) {
      Object.keys(action.payload).forEach(instructorId => {
        delete state.scheduleEditorData.addedInstructors[instructorId]
        delete state.scheduleEditorData.instructorWorkstations[instructorId]
      })
    },
    addEditorInstructors(
      state,
      action: PayloadAction<Record<string, SimulatorInstructorLedEnrolledPerson>>
    ) {
      state.scheduleEditorData.addedInstructors = {
        ...state.scheduleEditorData.addedInstructors,
        ...action.payload,
      }
    },
    setEditorInstructors(
      state,
      action: PayloadAction<Record<string, SimulatorInstructorLedEnrolledPerson>>
    ) {
      state.scheduleEditorData.addedInstructors = action.payload
    },
    updateSimulatorTrainingEnrollmentsRequested(
      state,
      action: PayloadAction<ApiMiddlewarePayload>
    ) {
      state.updateSimulatorTrainingEnrollmentsRequested = action.payload
    },
    deleteScheduleRequested(state, action: PayloadAction<ApiMiddlewarePayload>) {
      state.deleteScheduleRequested = action.payload
    },
    updateScheduleRequested(state, action: PayloadAction<ApiMiddlewarePayload>) {
      state.updateScheduleRequested = action.payload
    },
    addEditorNewSession(state, action: PayloadAction<SimulatorTrainingScheduleSession>) {
      const { sessionId } = action.payload
      state.scheduleEditorData.addedSessions[sessionId] = action.payload
    },
    removeEditorNewSession(state, action: PayloadAction<string>) {
      delete state.scheduleEditorData.addedSessions[action.payload]
    },
    addEditorRemovedSession(state, action: PayloadAction<SimulatorTrainingScheduleSession>) {
      const { sessionId } = action.payload
      state.scheduleEditorData.removedSessions[sessionId] = action.payload
    },
    addEditorUpdatedSession(state, action: PayloadAction<SimulatorTrainingScheduleSession>) {
      const { sessionId } = action.payload
      state.scheduleEditorData.updatedSessions[sessionId] = action.payload
    },
    deleteEditorUpdatedSession(state, action: PayloadAction<string>) {
      delete state.scheduleEditorData.updatedSessions[action.payload]
    },
    clearEditorSessionChanges(state) {
      state.scheduleEditorData.addedSessions = initialState.scheduleEditorData.addedSessions
      state.scheduleEditorData.removedSessions = initialState.scheduleEditorData.removedSessions
      state.scheduleEditorData.updatedSessions = initialState.scheduleEditorData.updatedSessions
    },
    updateScheduleSessionsRequested(state, action: PayloadAction<ApiMiddlewarePayload>) {
      state.updateScheduleSessionsRequested = action.payload
    },
    setFoundScheduleOngoingSession(state, action: PayloadAction<boolean | undefined>) {
      state.foundScheduleOngoingSession = action.payload
    },
  },
  extraReducers: builder => {
    builder.addCase(userLoggedOut, () => {
      return initialState
    })
  },
})

const {
  scheduleListLoaded,
  listScheduleMaintenanceAndSessionsLoaded,
  createSimulatorTrainingScheduleRequested,
  demandSimulatorTrainingScheduleSessionRequested,
  updateSimulatorTrainingEnrollmentsRequested,
  deleteScheduleRequested,
  updateScheduleRequested,
  updateScheduleSessionsRequested,
  clearEditorSessionChanges,
} = schedulesSlice.actions
export const {
  setSchedulesTagFilters,
  clearScheduleList,
  clearScheduleCreatorData,
  clearScheduleEditorData,
  setCreatorInstructorWorkstation,
  setCreatorInstructorWorkstations,
  setCreatorStudentWorkstation,
  setCreatorStudentWorkstations,
  removeCreatorStudents,
  addCreatorStudents,
  addCreatorInstructors,
  removeCreatorInstructors,
  setCreatorTimeZone,
  setCreatorSubject,
  setCreatorRange,
  setCreatorSession,
  removeCreatorSession,
  setEditorTimeZone,
  setEditorSubject,
  setEditorRange,
  addEditorInstructorWorkstation,
  addEditorStudentWorkstation,
  removeEditorStudents,
  addEditorStudents,
  setEditorStudents,
  removeEditorInstructors,
  addEditorInstructors,
  setEditorInstructors,
  setEditorInstructorWorkstations,
  setEditorStudentWorkstations,
  updateCreatorSessionSubject,
  setCreatorBookingMode,
  addEditorNewSession,
  removeEditorNewSession,
  addEditorRemovedSession,
  addEditorUpdatedSession,
  deleteEditorUpdatedSession,
  setFoundScheduleOngoingSession,
} = schedulesSlice.actions
export default schedulesSlice.reducer

export const getUnassignedInstructorWorkstationsCount = createSelector(
  (instructorWorkplaces: number = 0, assignedWorkstations: UserWorkstations) => ({
    instructorWorkplaces,
    assignedWorkstations,
  }),
  ({ instructorWorkplaces, assignedWorkstations }) => {
    const workstations = new Set(Object.values(assignedWorkstations))
    workstations.delete(undefined)
    return instructorWorkplaces > 0 ? instructorWorkplaces - workstations.size : 0
  }
)

export const getCreatorAddedInstructors = createSelector(
  (addedInstructors: Record<string, SpecialUser>) => addedInstructors,
  addedInstructors => Object.values(addedInstructors)
)

export const getUnassignedStudentWorkstationsCount = createSelector(
  (traineeWorkplaces: number = 0, assignedWorkstations: UserWorkstations) => ({
    traineeWorkplaces,
    assignedWorkstations,
  }),
  ({ traineeWorkplaces, assignedWorkstations }) => {
    const workstations = new Set(Object.values(assignedWorkstations))
    workstations.delete(undefined)
    return traineeWorkplaces > 0 ? traineeWorkplaces - workstations.size : 0
  }
)

export const getCreatorAddedStudents = createSelector(
  (addedStudents: Record<string, Person>) => addedStudents,
  addedStudents => Object.values(addedStudents)
)

export const loadScheduleListData = (
  filters: SchedulesTagFilterItems,
  trainingId: Guid
): ApiAction => {
  return createApiAction({
    apiCalls: {
      list: () => getListSimulatorTrainingSchedules(filters, trainingId),
    },
    payloadAction: scheduleListLoaded,
    shouldShowGlobalLoading: false,
    getResultToKeep: ({ simulatorTrainingInstructorILSchedules }) =>
      simulatorTrainingInstructorILSchedules.scheduleListData.result,
  })
}

export const getInstructorSimulatorTrainingEnrollments = createSelector(
  (instructorWorkstations: UserWorkstations) => instructorWorkstations,
  (instructorWorkstations): SimulatorTrainingEnrollment[] => {
    return Object.keys(instructorWorkstations).reduce((enrollments, userId) => {
      const workstationNumber = Number(instructorWorkstations[userId])
      if (!workstationNumber) return enrollments
      enrollments.push({
        userId,
        enrollmentType: SimulatorTrainingPersonType.Instructor,
        workstationNumber,
      })
      return enrollments
    }, [] as SimulatorTrainingEnrollment[])
  }
)

export const getStudentSimulatorTrainingEnrollments = createSelector(
  (studentWorkstations: UserWorkstations) => studentWorkstations,
  (studentWorkstations): SimulatorTrainingEnrollment[] => {
    return Object.keys(studentWorkstations).reduce((enrollments, userId) => {
      const workstationNumber = Number(studentWorkstations[userId])
      if (!workstationNumber) return enrollments
      enrollments.push({
        userId,
        enrollmentType: SimulatorTrainingPersonType.Student,
        workstationNumber,
      })
      return enrollments
    }, [] as SimulatorTrainingEnrollment[])
  }
)

export const getSimulatorTrainingEnrollments = createSelector(
  (
    instructorEnrollments: SimulatorTrainingEnrollment[],
    studentEnrollments: SimulatorTrainingEnrollment[]
  ) => ({ instructorEnrollments, studentEnrollments }),
  ({ instructorEnrollments, studentEnrollments }): SimulatorTrainingEnrollment[] => [
    ...instructorEnrollments,
    ...studentEnrollments,
  ]
)

export const loadListScheduleMaintenanceAndSessionsData = (
  trainingId: Guid,
  params: ListScheduleSessionsModel
): ApiAction => {
  return createApiAction({
    apiCalls: {
      list: () => getListScheduleMaintenanceAndSessions(trainingId, params),
    },
    payloadAction: listScheduleMaintenanceAndSessionsLoaded,
  })
}

export const reloadListScheduleMaintenanceAndSessionsData = (
  trainingId: Guid,
  params: ListScheduleSessionsModel
): ApiAction => {
  return createApiAction({
    apiCalls: {
      list: () => getListScheduleMaintenanceAndSessions(trainingId, params),
    },
    payloadAction: listScheduleMaintenanceAndSessionsLoaded,
    shouldShowGlobalLoading: false,
    getResultToKeep: ({ simulatorTrainingInstructorILSchedules }) =>
      simulatorTrainingInstructorILSchedules.scheduleMaintenanceAndSessionsData.result,
  })
}

export const handleClearEditorSessionChanges =
  (response: ApiMiddlewarePayload): AppThunk =>
  async dispatch => {
    dispatch(listScheduleMaintenanceAndSessionsLoaded(response))
    !response.error && dispatch(clearEditorSessionChanges())
  }

export const reloadListScheduleMaintenanceAndSessionsDataAfterEditorUpdate = (
  trainingId: Guid,
  params: ListScheduleSessionsModel
): ApiAction => {
  return createApiAction({
    apiCalls: {
      list: () => getListScheduleMaintenanceAndSessions(trainingId, params),
    },
    payloadAction: listScheduleMaintenanceAndSessionsLoaded,
    shouldShowGlobalLoading: false,
    getResultToKeep: ({ simulatorTrainingInstructorILSchedules }) =>
      simulatorTrainingInstructorILSchedules.scheduleMaintenanceAndSessionsData.result,
    nextAction: handleClearEditorSessionChanges,
  })
}

const handleCreateSimulatorTrainingSchedule =
  (response: ApiMiddlewarePayload, params: { trainingId: Guid }): AppThunk =>
  async dispatch => {
    const { trainingId } = params
    const { error, result } = response
    if (!error && result) {
      dispatch(push(`${schedulesRoutes.list}/${trainingId}`))
    }
    dispatch(createSimulatorTrainingScheduleRequested(response))
  }

export const createSimulatorTrainingSchedule = (
  params: AddSimulatorTrainingScheduleModel
): ApiAction => {
  const { trainingId } = params
  return createApiAction({
    apiCalls: {
      request: () => addSimulatorTrainingSchedule(params),
    },
    payloadAction: createSimulatorTrainingScheduleRequested,
    nextAction: {
      action: handleCreateSimulatorTrainingSchedule,
      params: { trainingId },
    },
    shouldShowGlobalLoading: true,
    enableErrorNotification: true,
    successMessage: i18n.t(`${I18_PATH}.scheduleCreator.successBook`),
  })
}

const handleDemandSimulatorTrainingScheduleSession =
  (response: ApiMiddlewarePayload<ApiDemandSimulatorTrainingScheduleSessionResponse>): AppThunk =>
  async dispatch => {
    const { error, result } = response
    if (!error && result?.request) {
      const { scheduleId } = result.request
      dispatch(push(`${simulatorTrainingInstructorILTrainingDetailsRoutes.root}/${scheduleId}`))
    }
    dispatch(demandSimulatorTrainingScheduleSessionRequested(response))
  }

export const demandScheduleSession = (
  params: DemandSimulatorTrainingScheduleSessionModel
): ApiAction => {
  return createApiAction({
    apiCalls: {
      request: () => demandSimulatorTrainingScheduleSession(params),
    },
    payloadAction: demandSimulatorTrainingScheduleSessionRequested,
    nextAction: handleDemandSimulatorTrainingScheduleSession,
    shouldShowGlobalLoading: true,
    enableErrorNotification: true,
  })
}

export const getEnrolledPersonsPartition = createSelector(
  (enrolledPersons: SimulatorInstructorLedEnrolledPerson[] | undefined) => enrolledPersons || [],
  enrolledPersons =>
    CommonUtils.partition<SimulatorInstructorLedEnrolledPerson>(
      enrolledPersons,
      (person: SimulatorInstructorLedEnrolledPerson) =>
        person.personType === SimulatorTrainingPersonType.Instructor
    )
)

export const getIdsOfStudentsWithAssessment = createSelector(
  getEnrolledPersonsPartition,
  ([_, students]) => {
    const assessNames = SimulatorTrainingUtils.getAssessmentNames()
    return students
      .filter(({ status }) => status !== null && assessNames[status])
      .map(({ userId }) => userId)
  }
)

export const getEditorEnrolledPeople = createSelector(
  (people: Record<string, SimulatorInstructorLedEnrolledPerson>) => people,
  people => Object.values(people)
)

const handleSetDefaultEnrolledPeople =
  (response: ApiMiddlewarePayload<ApiListInstructorLedEnrollmentsResponse>): AppThunk =>
  async dispatch => {
    const { error, result } = response
    if (!error && result) {
      const { enrollments } = result.list
      let addedStudents: Record<string, SimulatorInstructorLedEnrolledPerson> = {}
      let addedInstructors: Record<string, SimulatorInstructorLedEnrolledPerson> = {}
      let instructorWorkstations: UserWorkstations = {}
      let studentWorkstations: UserWorkstations = {}
      const [instructors, students] = getEnrolledPersonsPartition(enrollments)

      instructors.forEach(person => {
        addedInstructors[person.userId] = person
        if (person.workStationNumber !== undefined) {
          instructorWorkstations[person.userId] = `${person.workStationNumber}`
        }
      })

      students.forEach(person => {
        addedStudents[person.userId] = person
        if (person.workStationNumber !== undefined) {
          studentWorkstations[person.userId] = `${person.workStationNumber}`
        }
      })

      dispatch(setEditorStudents(addedStudents))
      dispatch(setEditorInstructors(addedInstructors))
      dispatch(setEditorInstructorWorkstations(instructorWorkstations))
      dispatch(setEditorStudentWorkstations(studentWorkstations))
    }
    dispatch(listInstructorLedEnrollmentsLoaded(response))
  }

export const loadListScheduleEnrolledPersonsData = (scheduleId: Guid): ApiAction => {
  return createApiAction({
    apiCalls: {
      list: () => getListInstructorLedEnrollments(scheduleId),
    },
    payloadAction: listInstructorLedEnrollmentsLoaded,
    // TODO: prepare data before update request
    // (for ability disable update Enrollments
    // button without any changes)
    nextAction: handleSetDefaultEnrolledPeople,
  })
}

export const reloadListScheduleEnrolledPersonsData = (scheduleId: Guid): ApiAction => {
  return createApiAction({
    apiCalls: {
      list: () => getListInstructorLedEnrollments(scheduleId),
    },
    payloadAction: listInstructorLedEnrollmentsLoaded,
    nextAction: handleSetDefaultEnrolledPeople,
    getResultToKeep: ({ simulatorTrainingInstructor }) =>
      simulatorTrainingInstructor.listInstructorLedEnrollmentsData.result,
  })
}

export const handleUpdateTrainingEnrollments =
  (response: ApiMiddlewarePayload, params: { scheduleId: Guid }): AppThunk =>
  async dispatch => {
    const { scheduleId } = params
    dispatch(updateSimulatorTrainingEnrollmentsRequested(response))
    if (!response.error) {
      dispatch(reloadListScheduleEnrolledPersonsData(scheduleId) as MiddlewareApiAction)
    }
  }

export const updateTrainingEnrollments = (
  params: UpdateSimulatorTrainingEnrollmentsModel
): ApiAction => {
  const { scheduleId } = params
  return createApiAction({
    apiCalls: {
      request: () => updateSimulatorTrainingEnrollments(params),
    },
    payloadAction: updateSimulatorTrainingEnrollmentsRequested,
    nextAction: {
      action: handleUpdateTrainingEnrollments,
      params: { scheduleId },
    },
    shouldShowGlobalLoading: true,
    enableErrorNotification: true,
  })
}

export const getScheduleSessionMappedToPlanner = createSelector(
  (sessions: SimulatorTrainingScheduleSession[] = [], scheduleId: Guid | undefined) => ({
    sessions,
    scheduleId,
  }),
  ({ sessions, scheduleId }) => PlannerUtils.mapSessionToPlanner(sessions, scheduleId)
)

export const getScheduleMaintenancePeriodMappedToPlanner = createSelector(
  (maintenancePeriods: SimulatorTrainingMaintenancePeriod[] = []) => maintenancePeriods,
  maintenancePeriods => PlannerUtils.mapMaintenancePeriodsToPlanner(maintenancePeriods)
)

export const getPlannerSchedule = createSelector(
  (
    scheduleSessions: SimulatorTrainingScheduleSessionEvent[],
    scheduleMaintenancePeriods: SimulatorTrainingScheduleSessionEvent[]
  ) => ({ scheduleSessions, scheduleMaintenancePeriods }),
  ({ scheduleSessions, scheduleMaintenancePeriods }) => [
    ...scheduleSessions,
    ...scheduleMaintenancePeriods,
  ]
)

export const getEditorMergedScheduleSessions = createSelector(
  (sessions: SimulatorTrainingScheduleSession[] = [], sessionChanges: SessionChanges) => ({
    sessions,
    sessionChanges,
  }),
  ({ sessions, sessionChanges }) => {
    const { addedSessions, removedSessions, updatedSessions } = sessionChanges
    const changedSessions: SimulatorTrainingScheduleSession[] = sessions.reduce(
      (result, session) => {
        const { sessionId } = session
        if (removedSessions.hasOwnProperty(sessionId)) return result
        if (updatedSessions.hasOwnProperty(sessionId)) {
          result.push(updatedSessions[sessionId])
          return result
        }
        result.push(session)
        return result
      },
      [] as SimulatorTrainingScheduleSession[]
    )

    return [...changedSessions, ...Object.values(addedSessions)]
  }
)

export const getCreatorMergedScheduleSessions = createSelector(
  (
    scheduleSessions: SimulatorTrainingScheduleSession[] = [],
    sessions: Record<string, SimulatorTrainingScheduleSession>
  ) => ({ scheduleSessions, sessions }),
  ({ scheduleSessions, sessions }) => {
    const normalizedSessions = Object.keys(sessions).map(sessionId => sessions[sessionId])
    return [...scheduleSessions, ...normalizedSessions]
  }
)

export const getScheduleSessionChanges = createSelector(
  (
    addedSessions: Record<string, SimulatorTrainingScheduleSession>,
    removedSessions: Record<string, SimulatorTrainingScheduleSession>,
    updatedSessions: Record<string, SimulatorTrainingScheduleSession>
  ) => ({ addedSessions, removedSessions, updatedSessions }),
  data => data
)

export const getScheduleSessionDates = createSelector(
  (sessionDates: Record<string, SimulatorTrainingScheduleSession>) => sessionDates,
  sessionDates => {
    const normalizedSessionDates: SimulatorTrainingSessionDate[] = Object.keys(sessionDates).map(
      sessionId => {
        const { startDate, endDate, type, sessionName } = sessionDates[sessionId]
        return { startDate, endDate, sessionType: type, name: sessionName }
      }
    )
    return normalizedSessionDates
  }
)

export const checkIsDemandAccessAvailable = createSelector(
  (
    sessions: SimulatorTrainingScheduleSession[],
    maintenancePeriods: SimulatorTrainingMaintenancePeriod[]
  ) => ({ sessions, maintenancePeriods }),
  ({ sessions, maintenancePeriods }) => {
    const sessionsNow = sessions.filter(({ startDate, endDate, type }) => {
      if (type === SimulatorTrainingSessionType.VirtualClassroom) return false
      return SchedulesUtils.isItCurrentSession(startDate, endDate)
    })
    const maintenancePeriodsNow = maintenancePeriods.filter(({ startDate, endDate }) => {
      return SchedulesUtils.isItCurrentSession(startDate, endDate)
    })

    return !sessionsNow.length && !maintenancePeriodsNow.length
  }
)

export const handleUpdateScheduleSessionsRequest =
  (response: ApiMiddlewarePayload): AppThunk =>
  async (dispatch, getState) => {
    dispatch(updateScheduleSessionsRequested(response))
    if (!response.error) {
      const { simulatorTrainingInstructor, simulatorTrainingInstructorILSchedules } = getState()
      const { scheduleDetailsData } = simulatorTrainingInstructor
      const { startDate, endDate } = simulatorTrainingInstructorILSchedules.scheduleEditorData
        .range as RangePeriod
      const trainingId = scheduleDetailsData.result?.details.trainingId || ''
      dispatch(
        reloadListScheduleMaintenanceAndSessionsDataAfterEditorUpdate(trainingId, {
          startDate,
          endDate,
        }) as MiddlewareApiAction
      )
    }
  }

export const updateScheduleSessionsRequest = (
  params: UpdateSimulatorTrainingScheduleSessionsRequest
): ApiAction => {
  return createApiAction({
    apiCalls: {
      request: () => updateSimulatorTrainingScheduleSessions(params),
    },
    payloadAction: updateScheduleSessionsRequested,
    shouldShowGlobalLoading: true,
    enableErrorNotification: true,
    nextAction: handleUpdateScheduleSessionsRequest,
  })
}

const handleRedirectOnDeleteSchedule =
  (response: ApiMiddlewarePayload): AppThunk =>
  async (dispatch, getState) => {
    const { error } = response
    if (!error) {
      const { id } =
        getState().simulatorTrainingInstructor.trainingDetailsData.result?.details || {}
      dispatch(push(`${schedulesRoutes.list}/${id}`))
    }
    dispatch(deleteScheduleRequested(response))
  }

export const deleteSchedule = (scheduleId: Guid): ApiAction => {
  return createApiAction({
    apiCalls: {
      request: () => deleteSimulatorTrainingSchedule(scheduleId),
    },
    payloadAction: deleteScheduleRequested,
    nextAction: handleRedirectOnDeleteSchedule,
    shouldShowGlobalLoading: true,
    enableErrorNotification: true,
  })
}

export const updateSchedule = (scheduleId: Guid, subject: string): ApiAction => {
  return createApiAction({
    apiCalls: {
      request: () => updateSimulatorTrainingSchedule(scheduleId, subject),
    },
    payloadAction: updateScheduleRequested,
    shouldShowGlobalLoading: true,
    enableErrorNotification: true,
  })
}
